<template>
  <div :style="themePageStyle">
    <div class="container" style="padding-top:80px;padding-bottom:100px;max-width:920px;">

      <!-- Header -->
      <div class="d-flex align-items-center mb-3 flex-wrap gap-2">
        <button class="btn btn-sm btn-outline-secondary" @click="$router.go(-1)"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
        <h5 class="mb-0 fw-bold ms-1" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}">
          <i class="mdi mdi-shield-star me-2" style="color:#BFA164;"></i>Flockr Credit Passport
        </h5>
        <div class="ms-auto d-flex gap-2">
          <button class="btn btn-sm btn-warning" @click="statementOpen = true" :disabled="loading"><i class="mdi mdi-file-download me-1"></i>Download Statement</button>
          <button class="btn btn-sm btn-outline-primary" @click="shareOpen = true" :disabled="loading"><i class="mdi mdi-share-variant me-1"></i>Share with Lender</button>
        </div>
      </div>

      <div v-if="loading" class="text-center py-5">
        <i class="mdi mdi-loading mdi-spin" style="font-size:36px;color:#BFA164;"></i>
        <div class="mt-2" style="color:#6B6356;">Computing your Passport…</div>
      </div>

      <template v-else-if="passport">
        <!-- Score dial + tier -->
        <div class="cp-hero" :class="{dark:darkMode}">
          <div class="cp-hero-left">
            <div class="cp-dial-wrap">
              <svg viewBox="0 0 200 200" class="cp-dial">
                <circle cx="100" cy="100" r="80" stroke="#E8E2DA" stroke-width="14" fill="none" />
                <circle cx="100" cy="100" r="80"
                        :stroke="passport.tier_colour" stroke-width="14" fill="none"
                        stroke-linecap="round" transform="rotate(-90 100 100)"
                        :stroke-dasharray="dialArc" />
                <text x="100" y="98" text-anchor="middle" font-size="42" font-weight="800" :fill="darkMode?'#F5F0E8':'#1A1A1A'">{{ passport.score }}</text>
                <text x="100" y="122" text-anchor="middle" font-size="12" fill="#6B6356">out of 1000</text>
              </svg>
            </div>
            <div class="cp-tier-chip" :style="{background:passport.tier_colour}">
              <i class="mdi mdi-medal me-1"></i>{{ passport.tier }}
            </div>
            <div v-if="passport.score_delta_30d !== 0" class="mt-2" style="font-size:12px;color:#6B6356;">
              <i :class="passport.score_delta_30d > 0 ? 'mdi mdi-trending-up text-success' : 'mdi mdi-trending-down text-danger'"></i>
              {{ passport.score_delta_30d > 0 ? '+' : '' }}{{ passport.score_delta_30d }} vs last snapshot
            </div>
          </div>
          <div class="cp-hero-right">
            <div class="cp-hero-title">Your on-platform credit record</div>
            <p class="cp-hero-sub">
              Every transaction, KYC step, farm record, and rating shapes your Passport.
              Lenders (MFIs, SACCOs, banks) use it to fast-track loan approvals.
            </p>
            <div class="cp-stat-grid">
              <div><div class="cp-stat-v">KES {{ fmt(passport.stats.gmv_kes) }}</div><div class="cp-stat-l">Lifetime GMV</div></div>
              <div><div class="cp-stat-v">{{ passport.stats.orders_12mo }}</div><div class="cp-stat-l">Orders (12 mo)</div></div>
              <div><div class="cp-stat-v">{{ passport.stats.avg_rating || '—' }}</div><div class="cp-stat-l">Avg Rating</div></div>
              <div><div class="cp-stat-v">{{ ageYears }}</div><div class="cp-stat-l">Member For</div></div>
            </div>
          </div>
        </div>

        <!-- Pillars -->
        <div class="cp-card mt-3" :class="{dark:darkMode}">
          <h6 class="cp-card-title"><i class="mdi mdi-chart-donut me-1"></i>Your 5 Pillars</h6>
          <div v-for="p in passport.pillars" :key="p.code" class="cp-pillar">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div class="cp-pillar-label">{{ p.label }} <span class="cp-pillar-weight">({{ p.weight_pct }}%)</span></div>
              <div class="cp-pillar-val">{{ p.score }} / {{ p.max }}</div>
            </div>
            <div class="cp-pillar-bar">
              <div class="cp-pillar-fill" :style="{width: (p.score/p.max*100) + '%', background: passport.tier_colour}"></div>
            </div>
          </div>
        </div>

        <!-- Tips -->
        <div v-if="passport.tips && passport.tips.length" class="cp-card mt-3" :class="{dark:darkMode}">
          <h6 class="cp-card-title"><i class="mdi mdi-lightbulb-on-outline me-1"></i>How to Grow Your Score</h6>
          <div v-for="(t, idx) in passport.tips" :key="idx" class="cp-tip">
            <div class="cp-tip-delta">+{{ t.delta }}</div>
            <div class="cp-tip-body">{{ t.action }}</div>
          </div>
        </div>

        <!-- Trend -->
        <div class="cp-card mt-3" :class="{dark:darkMode}">
          <h6 class="cp-card-title"><i class="mdi mdi-chart-line me-1"></i>12-Month Trend</h6>
          <div v-if="!passport.trend || passport.trend.length < 2" style="font-size:13px;color:#6B6356;">
            Not enough history yet. Your first snapshot will land after tonight's refresh.
          </div>
          <svg v-else viewBox="0 0 400 130" class="cp-trend">
            <polyline
              :points="trendPoints"
              fill="none"
              :stroke="passport.tier_colour"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round" />
            <g v-for="(pt, idx) in trendDots" :key="idx">
              <circle :cx="pt.x" :cy="pt.y" r="3" :fill="passport.tier_colour" />
            </g>
            <text x="0"   y="128" font-size="9" fill="#6B6356">{{ trendFirstDate }}</text>
            <text x="400" y="128" font-size="9" fill="#6B6356" text-anchor="end">{{ trendLastDate }}</text>
          </svg>
        </div>

        <!-- Badges -->
        <div v-if="passport.badges && passport.badges.length" class="cp-card mt-3" :class="{dark:darkMode}">
          <h6 class="cp-card-title"><i class="mdi mdi-trophy me-1"></i>Badges Earned</h6>
          <div class="cp-badges">
            <div v-for="b in passport.badges" :key="b.code" class="cp-badge">
              <i :class="b.icon" :style="{color: passport.tier_colour}"></i>
              <span>{{ b.label }}</span>
            </div>
          </div>
        </div>

        <!-- Audit trail -->
        <div class="cp-card mt-3" :class="{dark:darkMode}">
          <h6 class="cp-card-title d-flex align-items-center">
            <i class="mdi mdi-eye-outline me-1"></i>Who Has Viewed My Passport
            <button class="btn btn-sm btn-outline-secondary ms-auto" @click="loadViews">
              <i class="mdi mdi-refresh"></i>
            </button>
          </h6>
          <div v-if="!views.length" style="font-size:13px;color:#6B6356;">
            No views yet. Shared links will appear here once a lender opens them.
          </div>
          <div v-else v-for="(v, idx) in views" :key="idx" class="cp-view-row">
            <div>
              <div style="font-weight:600;font-size:13px;">{{ v.label || 'Unlabelled share' }}</div>
              <div style="font-size:11px;color:#6B6356;">{{ v.viewer_ip || 'unknown' }} · {{ fDate(v.viewed_at) }}</div>
            </div>
            <div style="font-size:11px;color:#8A827A;text-align:right;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
              {{ (v.viewer_ua || '').substr(0, 40) }}
            </div>
          </div>
        </div>
      </template>

      <!-- Statement date-range modal -->
      <div v-if="statementOpen" class="cp-modal-backdrop" @click.self="statementOpen = false">
        <div class="cp-modal" :class="{dark:darkMode}">
          <button class="cp-modal-close" @click="statementOpen = false"><i class="mdi mdi-close"></i></button>
          <h6 class="cp-card-title"><i class="mdi mdi-file-download me-1"></i>Download Credit Statement</h6>
          <p style="font-size:13px;color:#6B6356;">Choose the period. The statement includes your Passport summary, transactions, wallet activity, vet payments, and farm records.</p>
          <div class="row g-2 mb-3">
            <div class="col-6">
              <label class="cp-label">From</label>
              <input type="date" v-model="stmtFrom" class="form-control form-control-sm" />
            </div>
            <div class="col-6">
              <label class="cp-label">To</label>
              <input type="date" v-model="stmtTo" class="form-control form-control-sm" />
            </div>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-warning flex-fill" @click="openStatement">
              <i class="mdi mdi-printer me-1"></i>Open Statement
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="statementOpen = false">Cancel</button>
          </div>
        </div>
      </div>

      <!-- Share with lender modal -->
      <div v-if="shareOpen" class="cp-modal-backdrop" @click.self="shareOpen = false">
        <div class="cp-modal" :class="{dark:darkMode}">
          <button class="cp-modal-close" @click="shareOpen = false"><i class="mdi mdi-close"></i></button>
          <h6 class="cp-card-title"><i class="mdi mdi-share-variant me-1"></i>Share with a Lender</h6>
          <p style="font-size:13px;color:#6B6356;">Generate a link that shows your live Passport. The lender always sees your current score.</p>
          <div class="mb-2">
            <label class="cp-label">Label (optional)</label>
            <input type="text" v-model="shareLabel" placeholder="e.g. Equity Bank, KWFT SACCO" class="form-control form-control-sm" />
          </div>
          <div class="mb-3">
            <label class="cp-label">Link expires in</label>
            <select v-model.number="shareDays" class="form-select form-select-sm">
              <option :value="7">7 days</option>
              <option :value="30">30 days (recommended)</option>
              <option :value="90">90 days</option>
              <option :value="365">1 year</option>
            </select>
          </div>
          <button class="btn btn-warning w-100" @click="createShare" :disabled="creatingShare">
            <i class="mdi mdi-link-variant-plus me-1"></i>{{ creatingShare ? 'Creating…' : 'Create Share Link' }}
          </button>

          <div v-if="shareUrlOut" class="mt-3 pt-3" style="border-top:1px solid #E8E2DA;">
            <div class="cp-label mb-1">Your link</div>
            <div class="cp-share-url">{{ shareUrlOut }}</div>
            <div class="d-flex gap-2 mt-2 flex-wrap">
              <button class="btn btn-sm btn-outline-primary" @click="copy(shareUrlOut)"><i class="mdi mdi-content-copy me-1"></i>Copy</button>
              <a :href="'sms:?body=' + encodeURIComponent(shareSmsBody)" class="btn btn-sm btn-outline-info"><i class="mdi mdi-message-text me-1"></i>SMS</a>
              <a :href="'https://wa.me/?text=' + encodeURIComponent(shareSmsBody)" target="_blank" class="btn btn-sm btn-success"><i class="mdi mdi-whatsapp me-1"></i>WhatsApp</a>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, flocker } from '@/api';

export default {
  name: 'CreditPassport',
  data() {
    return {
      darkMode: (document.documentElement.getAttribute('data-theme') === 'dark'),
      loading: true,
      passport: null,
      views: [],
      statementOpen: false,
      stmtFrom: this.defaultFrom(),
      stmtTo: new Date().toISOString().slice(0,10),
      shareOpen: false,
      shareLabel: '',
      shareDays: 30,
      creatingShare: false,
      shareUrlOut: ''
    };
  },
  computed: {
    themePageStyle() { return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'; },
    dialArc() {
      if (!this.passport) return '0 502';
      var circumference = 2 * Math.PI * 80;
      var pct = Math.max(0, Math.min(1, this.passport.score / 1000));
      return (pct * circumference) + ' ' + circumference;
    },
    ageYears() {
      if (!this.passport) return '—';
      var d = this.passport.stats.account_age_days;
      if (d < 30) return d + 'd';
      if (d < 365) return Math.round(d/30) + ' mo';
      return (d/365).toFixed(1) + ' yr';
    },
    trendPoints() {
      if (!this.passport || !this.passport.trend || this.passport.trend.length < 2) return '';
      var t = this.passport.trend;
      var w = 400, h = 100;
      var stepX = w / Math.max(1, t.length - 1);
      return t.map(function(pt, i){
        var y = h - (pt.score/1000) * h + 10;
        return (i*stepX).toFixed(1) + ',' + y.toFixed(1);
      }).join(' ');
    },
    trendDots() {
      if (!this.passport || !this.passport.trend) return [];
      var t = this.passport.trend;
      var w = 400, h = 100;
      var stepX = w / Math.max(1, t.length - 1);
      return t.map(function(pt, i){
        return { x: i*stepX, y: h - (pt.score/1000) * h + 10 };
      });
    },
    trendFirstDate() { return this.passport && this.passport.trend[0] ? this.fShortDate(this.passport.trend[0].snapshot_date) : ''; },
    trendLastDate() { return this.passport && this.passport.trend.length ? this.fShortDate(this.passport.trend[this.passport.trend.length - 1].snapshot_date) : ''; },
    shareSmsBody() {
      return "See my Flockr Credit Passport: " + this.shareUrlOut + " (live, updates as I transact)";
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    defaultFrom() {
      var d = new Date();
      d.setMonth(d.getMonth() - 12);
      return d.toISOString().slice(0,10);
    },
    async load() {
      this.loading = true;
      try {
        var f = new FormData();
        f.append('function', 'getMyCreditPassport');
        f.append('userId', sessionStorage.getItem('userId') || '');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.passport = r.data.data;
        }
      } catch (e) { console.error('load passport', e); }
      this.loading = false;
      this.loadViews();
    },
    async loadViews() {
      try {
        var f = new FormData();
        f.append('function', 'listPassportViews');
        f.append('userId', sessionStorage.getItem('userId') || '');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.views = r.data.data.views || [];
        }
      } catch (e) { console.error('views', e); }
    },
    openStatement() {
      var qs = 'from=' + encodeURIComponent(this.stmtFrom) + '&to=' + encodeURIComponent(this.stmtTo);
      var w = window.open('/credit-passport/statement?' + qs, '_blank');
      this.statementOpen = false;
    },
    async createShare() {
      this.creatingShare = true;
      try {
        var f = new FormData();
        f.append('function', 'createPassportShareToken');
        f.append('userId', sessionStorage.getItem('userId') || '');
        f.append('label', this.shareLabel);
        f.append('days', this.shareDays);
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.shareUrlOut = window.location.origin + '/credit-passport/view/' + r.data.data.token;
        }
      } catch (e) { console.error('share', e); }
      this.creatingShare = false;
    },
    copy(text) {
      if (!text) return;
      try { navigator.clipboard.writeText(text); }
      catch (e) {
        var ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch(_){}
        document.body.removeChild(ta);
      }
    },
    fmt(n) { return new Intl.NumberFormat('en-KE').format(Math.round(Number(n) || 0)); },
    fDate(d) { if (!d) return ''; return new Date(d).toLocaleString('en-KE', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }); },
    fShortDate(d) { if (!d) return ''; return new Date(d).toLocaleDateString('en-KE', { day:'numeric', month:'short' }); }
  }
};
</script>

<style scoped>
.cp-hero { display:flex; gap:20px; background:#FFFFFF; border:1px solid #E8E2DA; border-radius:14px; padding:20px; flex-wrap:wrap; }
.cp-hero.dark { background:#141414; border-color:#1E1E1E; }
.cp-hero-left { text-align:center; min-width:200px; }
.cp-hero-right { flex:1; min-width:220px; }
.cp-dial-wrap { width:200px; height:200px; margin:0 auto; }
.cp-dial { width:100%; height:100%; }
.cp-tier-chip { display:inline-flex; align-items:center; padding:4px 12px; border-radius:20px; color:#fff; font-weight:700; font-size:13px; margin-top:8px; }
.cp-hero-title { font-size:18px; font-weight:700; margin-bottom:6px; }
.cp-hero-sub { font-size:13px; color:#6B6356; margin-bottom:14px; }
.cp-hero.dark .cp-hero-sub { color:#A09888; }
.cp-stat-grid { display:grid; grid-template-columns:repeat(2, 1fr); gap:10px; }
.cp-stat-v { font-size:16px; font-weight:800; color:#BFA164; font-family:'Courier New',monospace; }
.cp-stat-l { font-size:10px; color:#6B6356; text-transform:uppercase; letter-spacing:.5px; }

.cp-card { background:#FFFFFF; border:1px solid #E8E2DA; border-radius:12px; padding:16px; }
.cp-card.dark { background:#141414; border-color:#1E1E1E; }
.cp-card-title { font-size:14px; font-weight:700; margin-bottom:12px; }

.cp-pillar { margin-bottom:12px; }
.cp-pillar-label { font-size:13px; font-weight:600; }
.cp-pillar-weight { font-size:11px; color:#6B6356; font-weight:400; }
.cp-pillar-val { font-size:12px; color:#6B6356; font-family:'Courier New',monospace; }
.cp-pillar-bar { height:8px; background:#EDE7DE; border-radius:6px; overflow:hidden; }
.cp-card.dark .cp-pillar-bar { background:#1E1E1E; }
.cp-pillar-fill { height:100%; border-radius:6px; transition:width .3s; }

.cp-tip { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid #F0EBE4; }
.cp-card.dark .cp-tip { border-bottom-color:#1E1E1E; }
.cp-tip:last-child { border-bottom:0; }
.cp-tip-delta { min-width:44px; text-align:center; background:rgba(191,161,100,.12); color:#BFA164; font-weight:700; font-size:12px; padding:4px 6px; border-radius:6px; }
.cp-tip-body { font-size:13px; }

.cp-trend { width:100%; height:130px; }

.cp-badges { display:flex; flex-wrap:wrap; gap:8px; }
.cp-badge { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; background:#F7F5F2; border-radius:20px; font-size:12px; font-weight:600; }
.cp-card.dark .cp-badge { background:#1A1510; }

.cp-view-row { display:flex; justify-content:space-between; gap:10px; padding:8px 0; border-bottom:1px solid #F0EBE4; }
.cp-card.dark .cp-view-row { border-bottom-color:#1E1E1E; }
.cp-view-row:last-child { border-bottom:0; }

.cp-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.55); display:flex; align-items:center; justify-content:center; z-index:9000; padding:16px; }
.cp-modal { background:#FFFFFF; border-radius:14px; padding:24px; width:100%; max-width:440px; position:relative; }
.cp-modal.dark { background:#141414; color:#F5F0E8; }
.cp-modal-close { position:absolute; top:8px; right:10px; background:transparent; border:none; font-size:22px; color:#6B6356; cursor:pointer; }
.cp-label { font-size:11px; color:#6B6356; text-transform:uppercase; letter-spacing:.5px; font-weight:600; margin-bottom:4px; }
.cp-share-url { background:#F7F5F2; padding:8px 10px; border-radius:6px; font-size:11px; word-break:break-all; font-family:'Courier New',monospace; }
.cp-modal.dark .cp-share-url { background:#1A1510; }
</style>
